import React from 'react';
import { User, Briefcase, Star, Trash2, ChevronRight, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import AddToJobModal from './AddToJobModal';
import ConfirmationModal from './ConfirmationModal';
import { useState } from 'react';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function ResultsList({ results, onPreview, onDelete, searchedSkills = [] }) {
  const [jobCandidate, setJobCandidate] = useState(null);

  const [deleteId, setDeleteId] = useState(null);

  const handleDeleteRequest = (e, id) => {
    e.stopPropagation();
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_BASE}/resume/${deleteId}`);
      toast.success("Resume deleted");
      onDelete(deleteId);
    } catch (error) {
      toast.error("Failed to delete resume");
      console.error(error);
    }
    setDeleteId(null);
  };

  const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-4">
      <div className="flex justify-between items-center px-2">
        <h3 className="font-semibold text-slate-700 dark:text-slate-300">Found {results.length} Matches</h3>
      </div>

      {results.map((r) => (
        <motion.div
          key={r._id || r.resume_id}
          variants={item}
          layoutId={r._id || r.resume_id}
          className="group relative bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-brand-200 transition-all cursor-pointer overflow-hidden dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600"
          onClick={() => onPreview(r._id || r.resume_id, r.name, r)}
        >
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500 opacity-0 group-hover:opacity-100 transition-opacity" />

          <div className="flex justify-between items-start">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-brand-50 text-brand-600 rounded-full dark:bg-brand-900/30 dark:text-brand-300">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-slate-800 dark:text-slate-100">{r.name || 'Unknown Candidate'}</h4>
                  <p className="text-xs text-slate-500 flex items-center gap-1 dark:text-slate-400">
                    <Mail className="h-3 w-3" /> {r.email || 'No email provided'}
                  </p>
                </div>
              </div>

              <div className="flex gap-4 text-sm mt-2">
                <div className="flex items-center text-slate-600 bg-slate-50 px-2 py-1 rounded-md border border-slate-100 dark:bg-slate-700/50 dark:border-slate-700 dark:text-slate-300">
                  <Briefcase className="mr-1.5 h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                  <span className="font-medium">{r.years_experience} y.o.e</span>
                </div>
                <div className={clsx(
                  "flex items-center px-2 py-1 rounded-md border text-sm font-medium",
                  (r.search_score || 0) > 0.85
                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                    : (r.search_score || 0) > 0.70
                      ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                      : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-700/50 dark:text-slate-400 dark:border-slate-700"
                )}>
                  <Star className={clsx("mr-1.5 h-3.5 w-3.5", (r.search_score || 0) > 0.85 ? "fill-current" : "")} />
                  <span>Match: {((r.search_score || 0) * 100).toFixed(0)}%</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 pt-1">
                {r.skills && (() => {
                  const sortedSkills = [...r.skills].sort((a, b) => {
                    const aMatch = searchedSkills.some(s => s.toLowerCase() === a.toLowerCase());
                    const bMatch = searchedSkills.some(s => s.toLowerCase() === b.toLowerCase());
                    if (aMatch && !bMatch) return -1;
                    if (!aMatch && bMatch) return 1;
                    return 0;
                  });
                  return sortedSkills.slice(0, 6).map((skill, i) => {
                    const isHighlighted = searchedSkills.some(s => s.toLowerCase() === skill.toLowerCase());
                    return (
                      <span
                        key={i}
                        className={clsx(
                          "text-xs px-2 py-0.5 rounded-full border transition-all",
                          isHighlighted
                            ? "bg-brand-600 text-white border-brand-600 shadow-sm shadow-brand-500/50 scale-105 font-medium"
                            : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"
                        )}
                      >
                        {skill}
                      </span>
                    );
                  });
                })()}
                {r.skills && r.skills.length > 6 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500">+{r.skills.length - 6} more</span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 items-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setJobCandidate(r);
                }}
                className="p-2 text-slate-300 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors dark:hover:bg-brand-900/30"
                title="Add to Job Pipeline"
              >
                <Briefcase className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => handleDeleteRequest(e, r._id || r.resume_id)}
                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors dark:hover:bg-red-900/30"
                title="Delete Resume"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <div className="mt-auto opacity-0 group-hover:opacity-100 transition-opacity text-brand-600 flex items-center text-sm font-medium dark:text-brand-400">
                View <ChevronRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </div>
        </motion.div>
      ))}

      {jobCandidate && (
        <AddToJobModal
          candidate={jobCandidate}
          onClose={() => setJobCandidate(null)}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title="Delete Resume?"
        message="Are you sure you want to delete this resume? This action cannot be undone."
        confirmText="Delete Resume"
      />
    </motion.div>
  );
}
